import { selectOptimalRateChange } from '@/features/reports/components/shared/utils/rate-change-utils';
import { formatDateForNotes } from '@/helpers/date-helpers';
import { AI_NOTES_THRESHOLDS } from '../config/thresholds';
import type { RateAnalysis, RateShopData } from '../schemas';

export interface ProcessedRateShopData {
  hasSignificantChanges: boolean;
  formattedSummaryItems: string[];
}

/**
 * Enhanced rate shop analyzer that pre-calculates formatted output
 * Removes formatting logic from LLM prompts
 *
 * Uses meeting-type-aware rate change selection:
 * - DAILY meetings: prioritizes 1-day changes (or 3-day on Mondays)
 * - WEEKLY meetings: prioritizes 7-day changes
 */
export function analyzeRateShopData(data?: RateShopData, meetingCategory?: string): RateAnalysis {
  const analysis: RateAnalysis = {
    byDate: {},
    byCompetitor: {},
  };

  if (!data?.rows) return analysis;

  // Process each row (date)
  data.rows.forEach((row) => {
    const dateCell = row.find((cell) => cell.dataKey === 'stay_date');
    const dowCell = row.find((cell) => cell.dataKey === 'dow');
    // Combine date and day of week
    const date = dateCell?.stayDate ? `${dateCell.stayDate} ${dowCell?.value || ''}` : '';
    const clientData = row.find((cell) => cell.meta?.header === 'Client');
    const clientRate = clientData?.value || 0;

    // Skip if no client rate or invalid date
    if (!clientRate || !date) return;

    // Initialize date entry if needed
    if (!analysis.byDate[date]) {
      analysis.byDate[date] = {
        noRateCompetitors: [],
        significantChanges: [],
      };
    }

    // Process each competitor in the row
    row.forEach((cell) => {
      // Skip non-competitor cells
      if (!cell.meta?.competitor_name || cell.meta?.header === 'Client') return;

      const competitorName = cell.meta?.competitor_name;
      if (!competitorName) return; // Type guard

      // Initialize competitor entry if needed
      if (!analysis.byCompetitor[competitorName]) {
        analysis.byCompetitor[competitorName] = {
          noRatesDates: [],
          significantChanges: [],
        };
      }

      // Check for sellouts - only report if we have competitor's previous rate
      // Use meeting-type-aware selection to get the appropriate previous rate
      if (!cell.value && cell.meta.data?.changes?.length) {
        const selectedChange = selectOptimalRateChange(cell.meta.data.changes, meetingCategory);
        const competitorPreviousRate = selectedChange?.change?.value;

        // Only track sellout if competitor had a valid previous rate
        if (competitorPreviousRate && competitorPreviousRate > 0) {
          analysis.byDate[date].noRateCompetitors.push({
            competitorName,
            previousRate: competitorPreviousRate,
          });
          analysis.byCompetitor[competitorName].noRatesDates.push({
            date,
            previousRate: competitorPreviousRate,
          });
        }
      }

      // Check for significant rate changes
      // Use meeting-type-aware selection to get the appropriate comparison rate
      if (cell.meta.data?.changes?.length && cell.value) {
        const selectedChange = selectOptimalRateChange(cell.meta.data.changes, meetingCategory);
        if (selectedChange?.change?.value) {
          const prevRate = selectedChange.change.value;
          const newRate = cell.value;
          const percentChange = (newRate - prevRate) / prevRate;
          const rateDiff = Number((newRate - prevRate).toFixed(2));

          // Use centralized threshold - exclude sellout/availability transitions
          // Only flag actual rate changes between two available rates
          if (
            prevRate > 0 &&
            newRate > 0 &&
            Math.abs(percentChange) >= AI_NOTES_THRESHOLDS.RATE_SHOP_ALERT_PERCENTAGE
          ) {
            const change = {
              prevRate,
              newRate,
              rateDiff,
              percentChange,
            };

            // Add to date-based analysis
            analysis.byDate[date].significantChanges.push({
              competitorName,
              ...change,
            });

            // Add to competitor-based analysis
            analysis.byCompetitor[competitorName].significantChanges.push({
              date,
              ...change,
            });
          }
        }
      }
    });
  });

  return analysis;
}

/**
 * Pre-processes rate shop data into formatted summary items for LLM
 * Removes date formatting and grouping logic from prompts
 */
export function processRateShopForNotes(rateAnalysis: RateAnalysis): ProcessedRateShopData {
  const summaryItems: string[] = [];

  // Group by date and format
  Object.entries(rateAnalysis.byDate).forEach(([dateKey, dateEntry]) => {
    const formattedDate = formatDateForNotes(dateKey.split(' ')[0]); // Extract YYYY-MM-DD part

    // Handle sellouts (no rate competitors)
    if (dateEntry.noRateCompetitors.length > 0) {
      const competitorNames = dateEntry.noRateCompetitors.map((comp) => comp.competitorName);
      if (competitorNames.length === 1) {
        summaryItems.push(
          `${competitorNames[0]} sold out on ${formattedDate}, previous rate was $${Math.round(dateEntry.noRateCompetitors[0].previousRate)}`,
        );
      } else {
        summaryItems.push(
          `Multiple properties sold out on ${formattedDate}: ${competitorNames.join(', ')}`,
        );
      }
    }

    // Handle significant rate changes
    if (dateEntry.significantChanges.length > 0) {
      // Group by rate change type
      const increases = dateEntry.significantChanges.filter((change) => change.rateDiff > 0);
      const decreases = dateEntry.significantChanges.filter((change) => change.rateDiff < 0);

      if (increases.length > 0) {
        const rateIncreases = increases
          .map((change) => `${change.competitorName} (+$${Math.round(Math.abs(change.rateDiff))})`)
          .join(', ');
        summaryItems.push(`Rate increases on ${formattedDate}: ${rateIncreases}`);
      }

      if (decreases.length > 0) {
        const rateDecreases = decreases
          .map((change) => `${change.competitorName} (-$${Math.round(Math.abs(change.rateDiff))})`)
          .join(', ');
        summaryItems.push(`Rate reductions on ${formattedDate}: ${rateDecreases}`);
      }
    }
  });

  return {
    hasSignificantChanges: summaryItems.length > 0,
    formattedSummaryItems: summaryItems,
  };
}
